// Os filtros de CANAL e de PRODUTO do histórico de atendimentos.
//
// ## O pedido
//
// "Filtros no histórico para ver separadamente: Chat, WhatsApp, Sysfar e MAX"
// (Gisele, ponto 13 de 27/10/2026).
//
// Os quatro vieram na mesma frase, mas são duas perguntas diferentes:
//
//   - **canal** é por onde a conversa chegou -- site ou WhatsApp;
//   - **Sysfar e MAX são LINHA DE PRODUTO**, e vêm da empresa do cliente.
//
// Por isso são dois seletores que combinam, e não um de quatro opções: "é
// WhatsApp OU é MAX" não é o que alguém quer perguntar ao analisar resultado.
//
// ## Nenhuma regra nova
//
// O canal sai de `passaNoCanal` (que por sua vez chama `ehSalaDeWhatsapp`, o
// mesmo predicado que escolhe o ícone de cada linha) e a linha sai de
// `linhaDoProduto`, o mesmo util do chip do cliente. Reescrever qualquer uma
// das duas aqui faria o filtro esconder conversa que a própria tela desenha
// como sendo daquele canal ou daquela linha.

import { passaNoCanal } from './canalDaConversa';
import { linhaDoProduto } from './linhaDoProduto';

/** O valor do seletor quando não se quer recortar por produto. */
export const LINHA_TODAS = 'todas';

/** A linha de produto de uma conversa, ou `null` quando não dá para saber. */
export function linhaDaConversa(sala) {
    return linhaDoProduto(sala?.client?.company);
}

/**
 * A conversa passa no filtro de produto?
 *
 * Sem escolha, passa tudo -- é "filtro em branco", e não "filtro que não
 * aceita nada".
 */
export function passaNaLinha(sala, linha) {
    if (!linha || linha === LINHA_TODAS) return true;

    // Normaliza os dois lados pela MESMA função: o valor do seletor sai desta
    // lista, mas comparar string crua quebraria no dia em que alguém passasse
    // "MAX" em vez de "Max".
    return linhaDaConversa(sala) === linhaDoProduto({ product_description: linha });
}

/**
 * A nota de uma conversa, como NÚMERO, com "não avaliada" sendo 0.
 *
 * `rooms.evaluation` é nullable, e conversa sem avaliação vem `null`. O
 * seletor chama isso de "Sem avaliação" e usa o valor 0 -- os dois lados
 * passam por aqui para não existir um lugar que trate `null` e outro que não.
 */
export function notaDaConversa(sala) {
    return Number(sala?.evaluation) || 0;
}

/**
 * A conversa passa no filtro de avaliação?
 *
 * Lista VAZIA passa tudo: é "nenhuma nota escolhida", e não "nenhuma nota
 * aceita". Foi o que o seletor único fazia com o valor `all`, e trocar isso
 * ao virar múltiplo esvaziaria a tela de quem nunca tocou no filtro.
 *
 * @param {Array<number|string>} notas
 */
export function passaNaAvaliacao(sala, notas) {
    const escolhidas = Array.isArray(notas) ? notas : [];

    if (escolhidas.length === 0) return true;

    return escolhidas.map(Number).indexOf(notaDaConversa(sala)) !== -1;
}

/** O valor do departamento de uma conversa que não tem departamento. */
export const SEM_DEPARTAMENTO = 0;

/**
 * O id do departamento DA CONVERSA -- que nem sempre é o do operador.
 *
 * Sala sem departamento vira `SEM_DEPARTAMENTO` e não `null`: é um valor que
 * o seletor consegue oferecer, e sem ele essas conversas ficariam
 * inalcançáveis pelo filtro.
 */
export function departamentoDaConversa(sala) {
    return Number(sala?.service_group_id) || SEM_DEPARTAMENTO;
}

/**
 * A conversa passa no filtro de departamento?
 *
 * Mesma regra da avaliação: lista vazia passa tudo.
 *
 * @param {Array<number|string>} ids
 */
export function passaNoDepartamento(sala, ids) {
    const escolhidos = Array.isArray(ids) ? ids : [];

    if (escolhidos.length === 0) return true;

    return escolhidos.map(Number).indexOf(departamentoDaConversa(sala)) !== -1;
}

/**
 * Os departamentos que EXISTEM nas conversas carregadas.
 *
 * Sai do dado, e não da lista de departamentos cadastrados: oferecer um
 * departamento sem nenhuma conversa no período só daria um filtro que devolve
 * vazio -- é a mesma decisão de `linhasDoHistorico`.
 *
 * O rótulo vem de `service_group.description` (a tabela `service_groups` não
 * tem `name`), carregado por `Room::EAGER_LOAD_DO_DEPARTAMENTO` na api. Sem
 * essa relação no payload a lista sai só com "Sem departamento", porque o id
 * cru não diz nada a ninguém.
 *
 * "Sem departamento" fica no FIM, e não na ordem alfabética: ele não é um
 * departamento, é a ausência de um -- e em ordem alfabética ele cairia no meio
 * da lista, entre "Sem" e "Suporte". É o mesmo lugar que "Sem avaliação"
 * ocupa no seletor de notas.
 *
 * @returns {Array<{id: number, descricao: string}>} ordenados pelo rótulo
 */
export function departamentosDoHistorico(salas) {
    const lista = Array.isArray(salas) ? salas : [];
    const vistos = {};

    lista.forEach((sala) => {
        const id = departamentoDaConversa(sala);

        if (vistos[id]) return;

        vistos[id] = id === SEM_DEPARTAMENTO
            ? 'Sem departamento'
            : (sala?.service_group?.description || `#${id}`);
    });

    const todos = Object.keys(vistos).map((id) => ({ id: Number(id), descricao: vistos[id] }));

    /*
     * Ordenar os departamentos de verdade e ACRESCENTAR a ausência no fim, em
     * vez de pôr os dois no mesmo `sort` com um peso.
     *
     * Com o peso dentro do comparador o lugar da ausência passa a depender da
     * ordem em que o motor chama o comparador: medido, um peso de um lado só
     * dava a ordem certa para toda entrada que esta função consegue montar
     * (`Object.keys` devolve o id 0 primeiro), e portanto nenhum teste podia
     * distinguir o comparador correto do quebrado. Aqui o lugar é explícito.
     */
    const reais = todos
        .filter((d) => d.id !== SEM_DEPARTAMENTO)
        .sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR'));

    return reais.concat(todos.filter((d) => d.id === SEM_DEPARTAMENTO));
}

/**
 * As notas escolhidas, em TEXTO, para a caixa fechada e para o resumo.
 *
 * A caixa fechada de um `Select multiple` escreve os valores crus se ninguém
 * der um `renderValue`: "0, 3" em vez de "Sem avaliação, 3 estrelas".
 *
 * Ordena por nota (decrescente) e não pela ordem de clique: a caixa não pode
 * mudar de texto só porque a pessoa marcou as mesmas notas em outra ordem.
 *
 * @param {Array<number|string>} notas
 */
export function rotulosDasAvaliacoes(notas) {
    return (Array.isArray(notas) ? notas : [])
        .map(Number)
        .sort((a, b) => b - a)
        .map((nota) => {
            if (nota === 0) return 'Sem avaliação';

            return nota === 1 ? '1 estrela' : `${nota} estrelas`;
        })
        .join(', ');
}

/**
 * Os departamentos escolhidos, por NOME, pelo mesmo motivo.
 *
 * Id que não está na lista disponível vira `#id` em vez de sumir -- sumir
 * faria a caixa dizer menos departamentos do que está filtrando.
 *
 * @param {Array<number|string>} ids
 * @param {Array<{id: number, descricao: string}>} disponiveis
 */
export function rotulosDosDepartamentos(ids, disponiveis) {
    const lista = Array.isArray(disponiveis) ? disponiveis : [];

    return (Array.isArray(ids) ? ids : [])
        .map((id) => {
            const achado = lista.filter((d) => Number(d.id) === Number(id))[0];

            return achado ? achado.descricao : `#${id}`;
        })
        .join(', ');
}

/**
 * As conversas que passam em TODOS os filtros de recorte.
 *
 * Os quatro moram aqui, e não três aqui e um na tela: a `createRows` tinha o
 * de avaliação solto e reatribuía a lista a partir de `props.historyRooms` no
 * meio da cadeia, o que apagava em silêncio os filtros já aplicados.
 *
 * @param {Array} salas
 * @param {{canal?: string, linha?: string, avaliacoes?: Array, departamentos?: Array}} filtros
 */
export function filtrarHistorico(salas, filtros = {}) {
    const lista = Array.isArray(salas) ? salas : [];

    return lista.filter(
        (sala) => passaNoCanal(sala, filtros.canal)
            && passaNaLinha(sala, filtros.linha)
            && passaNaAvaliacao(sala, filtros.avaliacoes)
            && passaNoDepartamento(sala, filtros.departamentos)
    );
}

/**
 * As linhas de produto que EXISTEM nas conversas carregadas.
 *
 * Sai do dado, e não de uma lista fixa `['Sysfar', 'Max', 'Next']`: a linha
 * vem do ERP, e cravar as três esconderia a linha nova justamente no dia em
 * que ela aparecesse -- que é quando alguém quer filtrar por ela. É a mesma
 * decisão de `Company::linhasDeProdutoConhecidas` na api.
 *
 * @returns {string[]} ordenadas, para o seletor não mudar de ordem
 */
export function linhasDoHistorico(salas) {
    const lista = Array.isArray(salas) ? salas : [];
    const vistas = {};

    lista.forEach((sala) => {
        const linha = linhaDaConversa(sala);

        if (linha) vistas[linha] = true;
    });

    return Object.keys(vistas).sort();
}
